import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import type { DataTableCellValue } from 'src/components/data-table/DataTable.types';
import { FilterCheckboxList } from 'src/components/data-table/filter-checkbox-list/FilterCheckboxList';
import { usePageFilter } from 'src/contexts/page-filter-context';
import type { PageFilterRegistration } from 'src/contexts/page-filter-context';
import { resolveEntityFilters } from 'src/filters/entity-filter-registry';
import type { ResolvedFilterColumn } from 'src/filters/entity-filter-registry';

import { i18n } from './TopBarSearch.i18n';
import { useStyles } from './TopBarSearch.styles';
import type { GlobalSearchFilters, TopBarSearchEntityType } from './use-top-bar-search';
import { TOP_BAR_SEARCH_GROUP_ORDER } from './use-top-bar-search';

const GROUP_LABELS: Record<TopBarSearchEntityType, MessageDescriptor> = {
    instance: i18n.groupInstances,
    addon: i18n.groupSubscriptions,
    workspace: i18n.groupWorkspaces,
    dataset: i18n.groupDatasets,
    user: i18n.groupUsers,
    supportPackage: i18n.groupSupportPackages,
};

type FilterTab = 'page' | 'all';

interface GlobalFilterGroup {
    type: TopBarSearchEntityType;
    label: string;
    columns: ResolvedFilterColumn[];
}

/** Every selectable value for a column - used to reset a filter to "all selected". */
const allOptionValues = (col: ResolvedFilterColumn): DataTableCellValue[] =>
    col.filterOptions.map((o) => o.value);

/** Resets every column of a page registration to "all values selected". */
const resetRegistrationFilters = (registration: PageFilterRegistration): void => {
    registration.columns.forEach((col) => {
        registration.onFilterChange(col.field, allOptionValues(col));
    });
};

/** Builds the "all values selected" map for the global filter groups. */
const buildAllGlobalSelections = (groups: GlobalFilterGroup[]): GlobalSearchFilters => {
    const selections: GlobalSearchFilters = {};
    groups.forEach((group) => {
        group.columns.forEach((col) => {
            selections[`${group.type}.${col.field}`] = allOptionValues(col);
        });
    });
    return selections;
};

export interface SearchFilterDialogProps {
    open: boolean;
    onClose: () => void;
    globalFilters: GlobalSearchFilters;
    onGlobalFiltersChange: (update: (prev: GlobalSearchFilters) => GlobalSearchFilters) => void;
}

export const SearchFilterDialog = ({
    open,
    onClose,
    globalFilters,
    onGlobalFiltersChange,
}: SearchFilterDialogProps) => {
    const intl = useIntl();
    const { classes } = useStyles();

    const { registrations } = usePageFilter();
    const pageFilterGroups = useMemo(
        () => registrations.filter((r) => r.columns.length > 0),
        [registrations],
    );
    const hasPageFilters = pageFilterGroups.length > 0;

    const [filterTab, setFilterTab] = useState<FilterTab>('page');

    // Default to the page tab when filters exist, otherwise the all-filters tab.
    useEffect(() => {
        if (open) setFilterTab(hasPageFilters ? 'page' : 'all');
    }, [open, hasPageFilters]);

    const activeFilterCount = useMemo(
        () =>
            pageFilterGroups.reduce(
                (count, registration) =>
                    count +
                    registration.columns.filter((col) => {
                        const selected = registration.filters[col.field];
                        return (
                            selected !== undefined && selected.length !== col.filterOptions.length
                        );
                    }).length,
                0,
            ),
        [pageFilterGroups],
    );

    const globalFilterGroups = useMemo<GlobalFilterGroup[]>(
        () =>
            TOP_BAR_SEARCH_GROUP_ORDER.map((type) => ({
                type,
                label: intl.formatMessage(GROUP_LABELS[type]),
                columns: resolveEntityFilters(type, intl, 'global'),
            })),
        [intl],
    );

    const activeGlobalFilterCount = useMemo(
        () =>
            globalFilterGroups.reduce(
                (count, group) =>
                    count +
                    group.columns.filter((col) => {
                        const selected = globalFilters[`${group.type}.${col.field}`];
                        return (
                            selected !== undefined && selected.length !== col.filterOptions.length
                        );
                    }).length,
                0,
            ),
        [globalFilterGroups, globalFilters],
    );

    const handleClearAllFilters = useCallback(() => {
        pageFilterGroups.forEach(resetRegistrationFilters);
    }, [pageFilterGroups]);

    const handleGlobalFilterChange = useCallback(
        (field: string, values: DataTableCellValue[]) => {
            onGlobalFiltersChange((prev) => ({ ...prev, [field]: values }));
        },
        [onGlobalFiltersChange],
    );

    const handleClearAllGlobalFilters = useCallback(() => {
        onGlobalFiltersChange((prev) => ({
            ...prev,
            ...buildAllGlobalSelections(globalFilterGroups),
        }));
    }, [globalFilterGroups, onGlobalFiltersChange]);

    const activeTabFilterCount = filterTab === 'page' ? activeFilterCount : activeGlobalFilterCount;
    const handleClearActiveTabFilters =
        filterTab === 'page' ? handleClearAllFilters : handleClearAllGlobalFilters;

    const pageTabContent = hasPageFilters ? (
        <Box className={classes.filterGroupsList}>
            {pageFilterGroups.map((registration) => (
                <Box key={registration.id} className={classes.filterGroupRow}>
                    {pageFilterGroups.length > 1 && (
                        <Typography className={classes.filterColumnHeader}>
                            {registration.label}
                        </Typography>
                    )}
                    <Box className={classes.filterFieldsRow}>
                        {registration.columns.map((col) => (
                            <Box key={col.field} className={classes.filterFieldColumn}>
                                <Typography className={classes.filterFieldHeader}>
                                    {col.headerName}
                                </Typography>
                                <FilterCheckboxList
                                    field={col.field}
                                    options={col.filterOptions}
                                    selected={registration.filters[col.field]}
                                    onChange={registration.onFilterChange}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    ) : (
        <Typography variant="body2" color="text.secondary" className={classes.filterEmptyState}>
            {intl.formatMessage(i18n.noPageFilters)}
        </Typography>
    );

    const globalTabContent = (
        <Box className={classes.filterGroupsList}>
            {globalFilterGroups.map((group) => (
                <Box key={group.type} className={classes.filterGroupRow}>
                    <Typography className={classes.filterColumnHeader}>{group.label}</Typography>
                    <Box className={classes.filterFieldsRow}>
                        {group.columns.map((col) => (
                            <Box key={col.field} className={classes.filterFieldColumn}>
                                <Typography className={classes.filterFieldHeader}>
                                    {col.headerName}
                                </Typography>
                                <FilterCheckboxList
                                    field={`${group.type}.${col.field}`}
                                    options={col.filterOptions}
                                    selected={globalFilters[`${group.type}.${col.field}`]}
                                    onChange={handleGlobalFilterChange}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{ paper: { className: classes.filterDialogPaper } }}
            automation-id="top-bar-filter-dialog"
        >
            <DialogTitle className={classes.filterDialogTitle}>
                <Box className={classes.filterDialogTitleText}>
                    <FilterListIcon fontSize="small" />
                    {intl.formatMessage(i18n.filters)}
                </Box>
                <IconButton
                    size="small"
                    aria-label={intl.formatMessage(i18n.closeFilters)}
                    automation-id="top-bar-filter-dialog-close"
                    onClick={onClose}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Tabs
                value={filterTab}
                onChange={(_, value: FilterTab) => setFilterTab(value)}
                className={classes.filterDialogTabs}
                automation-id="top-bar-filter-tabs"
            >
                <Tab
                    value="page"
                    label={intl.formatMessage(i18n.filtersOnThisPage)}
                    disabled={!hasPageFilters}
                    automation-id="top-bar-filter-tab-page"
                />
                <Tab
                    value="all"
                    label={intl.formatMessage(i18n.allFilters)}
                    automation-id="top-bar-filter-tab-all"
                />
            </Tabs>
            <DialogContent dividers className={classes.filterDialogContent}>
                {filterTab === 'page' ? pageTabContent : globalTabContent}
            </DialogContent>
            <DialogActions className={classes.filterDialogActions}>
                {activeTabFilterCount > 0 && (
                    <Button
                        size="small"
                        onClick={handleClearActiveTabFilters}
                        automation-id="top-bar-filter-clear-all"
                    >
                        {intl.formatMessage(i18n.clearFilters)}
                    </Button>
                )}
                <Button
                    size="small"
                    variant="contained"
                    onClick={onClose}
                    automation-id="top-bar-filter-dialog-done"
                >
                    {intl.formatMessage(i18n.done)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
